import { PublicClientApplication } from "@azure/msal-browser";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import * as z from 'zod';
import { CookiesKeys, LoginPlatforms, pathDelmiter, ssoProperties, ssoPropertiesCIAM } from '../Constants/Consts';
import { msalConfig, msalConfigCIAM } from "../config/MsalConfig";
import { ChecklistTaskApiResponse, DDCheckList, DrilldownMappingAPIResponse, TaskNode, TasksRoot } from "../types/DataDD/DataDD";
import { PunchlistApiType } from "../types/PunchList/PunchListSchema";
import { ModelApiItem } from "../types/Model/ModelSchema";
import { TInspections, TInspectionTask, TInspectionTaskLink } from "../types/types";

export const splitOnCapitals = (name: string = "") => name && name.length ? name.split(/(?=[A-Z])/g)?.join(" ") : "";
export const commaSeparatedNumbers = (nums: string = "") => nums.split(",")?.map(n => Number(n))

// to intersept when needed
export const fetchApi = async (url: string, options?: RequestInit) => {
  const currentToken = Cookies.get(CookiesKeys.jwt)
  const decodedToken = jwtDecode(currentToken);
  const currentTime = Math.floor(Date.now() / 1000);
  const timeUntilExpiry = decodedToken.exp - currentTime;
  if (timeUntilExpiry <= 300) { // 5 minutes threshold
    // Token is about to expire, renew it using the correct MSAL config for the login platform
    const loginPlatform = Cookies.get(CookiesKeys.loginPlatform);
    const msalInstance = new PublicClientApplication(
      loginPlatform === LoginPlatforms.ciam ? msalConfigCIAM : msalConfig
    );
    await msalInstance.initialize()
    const accounts = msalInstance.getAllAccounts()
    if (loginPlatform === LoginPlatforms.azure) {
      const accessTokenRequest = {
        scopes: ssoProperties.scopes,
        account: accounts[0],
      };
      const newTokenResponse = await msalInstance.acquireTokenSilent(accessTokenRequest);
      Cookies.set(CookiesKeys.jwt, newTokenResponse.accessToken);
    }
    else if (loginPlatform === LoginPlatforms.ciam) {
      const accessTokenRequest = {
        scopes: ssoPropertiesCIAM.scopes,
        account: accounts[0],
      };
      const newTokenResponse = await msalInstance.acquireTokenSilent(accessTokenRequest);
      // CIAM APIs continue to accept the OIDC idToken as the bearer token
      Cookies.set(CookiesKeys.jwt, newTokenResponse.idToken);
    }
    else {
      //TODO redirect to landing page
      alert("you are not logged on")
    }
  }
  return fetch(url, {
    ...options,
    headers: {
      ...options?.headers,
      'Authorization': 'Bearer ' + Cookies.get(CookiesKeys.jwt),
    }
  })
};

export const zodKeys = <T extends z.ZodTypeAny>(schema: T): string[] => {
  if (schema === null || schema === undefined) return [];
  if (schema instanceof z.ZodNullable || schema instanceof z.ZodOptional) return zodKeys(schema.unwrap());
  if (schema instanceof z.ZodArray) return zodKeys(schema.element);
  if (schema instanceof z.ZodObject) {
    const entries = Object.entries(schema.shape);
    return entries.flatMap(([key, value]) => {
      const nested = value instanceof z.ZodType ? zodKeys(value).map(subKey => `${key}.${subKey}`) : [];
      return nested.length ? nested : key;
    });
  }
  return [];
};

const uuid = (a?): string => a ? (a ^ Math.random() * 16 >> a / 4).toString(16) : ([1e7, -1e3, -4e3, -8e3, -1e11].join('')).replace(/[018]/g, uuid);

export const getRandomUuid = () => crypto?.randomUUID?.() ?? uuid()

/**
 * Generates a sequential punch list tag for a user
 * Format: {idTagInitial}-{sequentialNumber}
 * @param idTagInitial - The user's ID tag initial from assignment (e.g., "WR-01")
 * @param existingPunchLists - Array of existing punch lists for the user
 * @returns A unique punch list tag (e.g., "WR-01-0001") or null if no idTagInitial
 */
export const generatePunchListTag = (
  idTagInitial: string,
  existingPunchLists: PunchlistApiType[]
): string | null => {

  if (!idTagInitial) {
    return null;
  }

  const userPunchLists = existingPunchLists.filter(pl =>
    pl.punchListTag?.startsWith(`${idTagInitial}-`)
  );

  let maxSequence = 0;
  userPunchLists.forEach(pl => {
    if (pl.punchListTag) {
      const parts = pl.punchListTag.split('-');
      const sequenceStr = parts[parts.length - 1];
      const sequence = parseInt(sequenceStr, 10);
      if (!isNaN(sequence) && sequence > maxSequence) {
        maxSequence = sequence;
      }
    }
  });

  const nextSequence = (maxSequence + 1).toString().padStart(4, '0');

  return `${idTagInitial}-${nextSequence}`;
}

export const blobToBase64 = (blob): Promise<string> => {
  const reader = new FileReader();
  reader.readAsDataURL(blob);
  return new Promise(resolve => {
    reader.onloadend = () => {
      resolve(reader.result as string);
    };
  });
};


export function deleteAllCookies() {
  document.cookie.split(';').forEach(cookie => {
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
    document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
  });
}


export function getByPath(path: string = "", obj: any) {
  const pathArray = path.split(pathDelmiter).filter(Boolean);
  let current = obj;
  for (const key of pathArray) {
    if (current[key] === undefined) {
      return current;
    }
    current = current[key];
  }
  return current;
}

export function getCompletePath(ChecklistItem: DDCheckList | ChecklistTaskApiResponse) {
  switch (ChecklistItem.drilldownTypeId) {
    case 1:
      return `${ChecklistItem.dd01}.${ChecklistItem.dd02}.${ChecklistItem.ctUuid}`
    case 2:
    case 4:
      return `${ChecklistItem.dd01}.${ChecklistItem.dd02}.${ChecklistItem.dd03}.${ChecklistItem.ctUuid}`
    default:
      return `${ChecklistItem.dd01}.${ChecklistItem.dd02}.${ChecklistItem.ctUuid}`
  }
}
export function getCompletePath2(ChecklistItem: DDCheckList | ChecklistTaskApiResponse) {
  const steps = getDrilldownStepsNumber(ChecklistItem)
  // RAD type 21 items with multiple dd levels live at dd01.checkItems[ctUuid] after flattenRADChilds.
  // Single-level items (steps === 1) still live at dd01.taskChilds[ctUuid] — handled by the loop.
  if (ChecklistItem.drilldownTypeId === 21 && steps > 1) {
    return `childs${pathDelmiter}${ChecklistItem.dd01}${pathDelmiter}checkItems${pathDelmiter}${ChecklistItem.ctUuid}`
  }
  let path = "childs"
  for (let i = 1; i <= steps; i++) {
    const atLastStep = i === steps
    const step = i < 10 ? `0${i}` : `${i}`
    const currentDD = ChecklistItem[`dd${step}`] // actual naming like CC20 -> CZA_540 -> ___
    path += pathDelmiter + currentDD
    if (atLastStep) {
      path += pathDelmiter + "taskChilds"
    } else path += pathDelmiter + "childs"
    if (atLastStep) path += pathDelmiter + ChecklistItem.ctUuid
  }
  return path
}

export function getDrilldownStepsNumber(ddo: DrilldownMappingAPIResponse | ChecklistTaskApiResponse) {
  let num = 0
  const ddoArray = Object.keys(ddo ?? {})
  for (const key of ddoArray) {
    if (key.includes("dd") && !!ddo[key]) {
      num++
    }
  }
  return num
}

function detectRADType(node: TaskNode): number | null {
  // Check nested childs first so deep items (type 21) take priority
  // over direct taskChilds items (type 22) when both exist at the same level.
  for (const child of Object.values(node.childs ?? {})) {
    const t = detectRADType(child)
    if (t != null) return t
  }
  if (node.taskChilds && Object.keys(node.taskChilds).length > 0) {
    const first = Object.values(node.taskChilds)[0] as ChecklistTaskApiResponse
    if (first?.drilldownTypeId != null) return first.drilldownTypeId
  }
  return null
}

function collectLeafTaskChilds(childs: Record<string, TaskNode>, result: Record<string, ChecklistTaskApiResponse>) {
  for (const key of Object.keys(childs)) {
    const node = childs[key]
    const hasNestedChilds = node.childs && Object.keys(node.childs).length > 0
    if (hasNestedChilds) {
      collectLeafTaskChilds(node.childs, result)
    } else if (node.taskChilds) {
      Object.assign(result, node.taskChilds)
    }
  }
}

// For RAD type 21: populates dd01.checkItems with a flat map of all leaf-level taskChilds items.
// dd01.taskChilds is left completely untouched. dd01.childs is set to null after flattening.
export function flattenRADChilds(root: TaskNode): TaskNode {
  if (!root.childs) return root
  for (const dd01Key of Object.keys(root.childs)) {
    const dd01Node = root.childs[dd01Key]
    if (!dd01Node.childs || Object.keys(dd01Node.childs).length === 0) continue
    const type = detectRADType(dd01Node)
    if (type !== 21) continue
    const leafItems: Record<string, ChecklistTaskApiResponse> = {}
    collectLeafTaskChilds(dd01Node.childs, leafItems)
    dd01Node.checkItems = leafItems
    dd01Node.childs = null
  }
  return root
}

export const buildChunks = (arr: string[], chunkSize: number) => Array.from(
  new Array(Math.ceil(arr.length / chunkSize)),
  (_, i) => arr.slice(i * chunkSize, i * chunkSize + chunkSize)
);

export function getTaskNodeAtSpecificLevelFromElement(element: ChecklistTaskApiResponse, rootObject: TasksRoot, level: number) {
  let currentObject: Record<string, ChecklistTaskApiResponse | TaskNode> = rootObject.childs;
  for (let i = 0; i < level; i++) {
    const dropDownKeyOfElement = element[`dd0${i + 1}`];
    if (!(currentObject[dropDownKeyOfElement] as TaskNode)?.childs) {
      throw new Error(`Invalid path at dropDownKeyOfElement: ${dropDownKeyOfElement}`);
    }
    currentObject = (currentObject[dropDownKeyOfElement] as TaskNode).childs;
  }
  return currentObject
}
export function getTaskNodeAtSpecificLevelFromElement2(element: ChecklistTaskApiResponse, rootObject: TaskNode, level: number) {
  let currentObject: Record<string, ChecklistTaskApiResponse | TaskNode> = rootObject.childs;
  for (let i = 0; i < level; i++) {
    const dropDownKeyOfElement = element[`dd0${i + 1}`];
    if (!(currentObject[dropDownKeyOfElement] as TaskNode)?.childs) {
      throw new Error(`Invalid path at dropDownKeyOfElement: ${dropDownKeyOfElement}`);
    }
    currentObject = (currentObject[dropDownKeyOfElement] as TaskNode).childs;
  }
  return currentObject
}

export function replaceLastOccurrence(path, delimiter, oldValue, newValue) {
  let parts = path.split(delimiter);
  for (let i = parts.length - 1; i >= 0; i--) {
    if (parts[i] === oldValue) {
      parts[i] = newValue;
      break;
    }
  }
  return parts.join(delimiter);
}

export function getDateMMDDYYYYFormat(date: Date) {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };
  return new Intl.DateTimeFormat('en-US', options).format(date);
}

export function setLocalStorageItem(key: string, value: any) {
  if (!key) return;
  localStorage.setItem(key, JSON.stringify(value));
}

export function getLocalStorageItem<T>(key: string): T | null {
  if (!key) return null;
  const localStorageKey = localStorage.getItem(key)
  const item = localStorageKey ? JSON.parse(localStorageKey) : null
  return item ?? null;
}

export function removeLocalStorageItem(key: string) {
  if (!key) return;
  localStorage.removeItem(key);
}

export function filteredUsers(rows: any[], searchTerm: string, isAdminUser: boolean, userId: string | number, projectName?: string) {
  const searchTermLower = searchTerm.trim().toLowerCase();
  return rows.filter(row => {
    if (!row.original) return false;

    const rowData = row.original;

    if (projectName || (projectName && searchTerm)) {
      return row?.original?.project.includes(projectName) && (
        (rowData.fullName && rowData.fullName.toLowerCase().includes(searchTermLower)) ||
        (rowData.kionEmail && String(rowData.kionEmail).includes(searchTermLower)) ||
        (rowData.disciplineId && String(rowData.disciplineId).includes(searchTermLower)) ||
        (rowData.sequence && rowData.sequence.toLowerCase().includes(searchTermLower)) ||
        rowData.discipline && rowData.discipline.toLowerCase().includes(searchTermLower))
    }

    if (!isAdminUser && rowData.id !== userId) return false;

    return (
      (rowData.fullName && rowData.fullName.toLowerCase().includes(searchTermLower)) ||
      (rowData.projectNumber && String(rowData.projectNumber).includes(searchTermLower)) ||
      (rowData.disciplineId && String(rowData.disciplineId).includes(searchTermLower)) ||
      (rowData.sequence && rowData.sequence.toLowerCase().includes(searchTermLower)) ||
      (rowData.kionEmail && rowData.kionEmail.toLowerCase().includes(searchTermLower)) ||
      (rowData.project && rowData.project.toLowerCase().includes(searchTermLower)) ||
      (rowData.discipline && rowData.discipline.toLowerCase().includes(searchTermLower))
    );
  });
}

export function filteredInspections(searchTerm: string, rows: any[]) {
  const searchTermLower = searchTerm?.trim().toLowerCase();
  return rows.filter(row => {
    if (!row.original) return false;
    const rowData = row.original;
    return (
      (rowData.modelDescription && rowData.modelDescription.toLowerCase().includes(searchTermLower)) ||
      (rowData.modelNumber && (rowData.modelNumber.toLowerCase()).includes(searchTermLower))
    );
  });
}

export function filteredProjects(isAdminUser: boolean, userId: string | number, searchTerm: string, rows: any[]) {
  const searchTermLower = searchTerm.trim().toLowerCase();
  return rows.filter(row => {
    if (!row.original) return false;
    const rowData = row.original;
    if (!isAdminUser && rowData.id !== userId) return false;
    return (
      (rowData.fullName && rowData.fullName.toLowerCase().includes(searchTermLower)) ||
      (rowData.projectNumber && String(rowData.projectNumber).includes(searchTermLower)) ||
      (rowData.projectName && rowData.projectName.toLowerCase().includes(searchTermLower)) ||
      (rowData.projectLocation && rowData.projectLocation.toLowerCase().includes(searchTermLower)) ||
      (rowData.country && rowData.country.toLowerCase().includes(searchTermLower)) ||
      (rowData.disciplineId && String(rowData.disciplineId).includes(searchTermLower)) ||
      (rowData.sequence && rowData.sequence.toLowerCase().includes(searchTermLower)) ||
      (rowData.kionEmail && rowData.kionEmail.toLowerCase().includes(searchTermLower)) ||
      (rowData.loginId && rowData.loginId.toLowerCase().includes(searchTermLower)) ||
      (rowData.project && rowData.project.toLowerCase().includes(searchTermLower)) ||
      (rowData.discipline && rowData.discipline.toLowerCase().includes(searchTermLower))
    );
  });
}

export function filteredInspectionTable(searchTerm: string, rows: any[]) {
  const searchTermLower = searchTerm?.trim().toLowerCase();
  return rows.filter(row => {
    if (!row.original) return false;
    const rowData = row.original;
    return (
      (rowData.inspectionTaskId && String(rowData.inspectionTaskId).toLowerCase().includes(searchTermLower)) ||
      (rowData.taskDescription && rowData.taskDescription.toLowerCase().includes(searchTermLower)) ||
      (rowData.taskHeader && rowData.taskHeader.toLowerCase().includes(searchTermLower)) ||
      (rowData.inspectionId && (rowData?.inspectionId?.toString().toLowerCase()).includes(searchTermLower))
    );
  });
}

export function filteredInspectionLinksTable(searchTerm: string, rows: any[]) {
  const searchTermLower = searchTerm?.trim().toLowerCase();
  return rows.filter(row => {
    if (!row.original) return false;
    const rowData = row.original;
    return (
      (rowData.linkText && (rowData.linkText).toLowerCase().includes(searchTermLower)) ||
      (rowData.linkCategory && rowData.linkCategory.toLowerCase().includes(searchTermLower))
    );
  });
}



/**
 * Finds inspections matching a model's modelNumber.
 * @param model - The model item.
 * @param inspections - Array of all inspections.
 * @returns Array of matching inspections ids.
 */
function findMatchingInspections(model: ModelApiItem, inspections: TInspections[]): number[] {
  return inspections.filter(inspection => inspection.modelNumber === model.modelNumber).map(inspection => inspection.inspectionId)
}

/**
 * Finds tasks matching an inspection ID.
 * @param inspectionId - The inspection ID.
 * @param inspectionsTask - Array of all inspection tasks.
 * @returns Array of matching tasks.
 */
function findMatchingTasks(inspectionId: number, inspectionsTask: TInspectionTask[]): TInspectionTask[] {
  return inspectionsTask.filter(task => +task.inspectionId === +inspectionId);
}

/**
 * Injects links into tasks immutably.
 * @param inspectionTasks - Array of inspectionTasks.
 * @param inspectionTaskLinks - Array of all task links.
 * @returns Array of tasks with injected links according to their inspectionTaskId property.
 */
function injectLinksIntoTasks(inspectionTasks: TInspectionTask[], inspectionTaskLinks: TInspectionTaskLink[]): TInspectionTask[] {
  return inspectionTasks.map(inspectionTask => ({
    ...inspectionTask,
    inspectionTaskLinks: inspectionTaskLinks.filter(link => link.inspectionTaskId === inspectionTask.inspectionTaskId)
  }));
}

/**
 * Injects inspection and task data into models for easier access.
 * @param models - Array of model items.
 * @param inspections - Array of inspections.
 * @param inspectionsTask - Array of inspection tasks.
 * @param inspectionTaskLinks - Array of inspection task links.
 * @returns Models with injected inspections and tasks.
 */

export function handleInjectInspectionTaskToModels(
  models: ModelApiItem[],
  inspections: TInspections[],
  inspectionsTask: TInspectionTask[],
  inspectionTaskLinks: TInspectionTaskLink[]
): ModelApiItem[] {
  return models.map(model => {
    const matchingInspections = findMatchingInspections(model, inspections);

    if (matchingInspections.length === 0)
      return { ...model, inspectionIds: [], inspectionTasks: [] };
    /* NOTE: We intentionally use only the first matching inspectionId when injecting tasks.
      At this time a model is expected to have at most one relevant/active inspection; if
      multiple IDs are returned, the first one from findMatchingInspections is treated as
      the primary inspection for task injection, while all IDs are still exposed via inspectionIds. */
    const inspectionId = matchingInspections[0];
    const matchingInspectionTasks = findMatchingTasks(inspectionId, inspectionsTask);
    const updatedInspectionTasks = injectLinksIntoTasks(matchingInspectionTasks, inspectionTaskLinks);

    return {
      ...model,
      inspectionIds: matchingInspections,
      inspectionTasks: updatedInspectionTasks
    };
  });
}